'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

import { useAppAuth } from '@/hooks/use-app-auth';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ROUTES } from '@/lib/routes';

export function AffiliateApplicationForm() {
  const { session, loading } = useAppAuth();
  const [form, setForm] = useState({ name: '', websiteUrl: '', socialUrl: '', audienceDescription: '', promotionPlan: '', legalTermsAccepted: false, disclosureAccepted: false, antiSpamAccepted: false });
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!session) return;
    fetch('/api/affiliate/application', { credentials: 'include' }).then((response) => response.json()).then((payload) => {
      if (payload.application?.status) setStatus(payload.application.status);
    }).catch(() => null);
  }, [session]);

  async function submit() {
    setWorking(true); setError(null);
    try {
      const response = await fetch('/api/affiliate/application', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Unable to submit your application.');
      setStatus(payload.application.status);
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to submit your application.'); }
    finally { setWorking(false); }
  }

  if (loading) return <p className="text-sm text-charcoal/70">Checking your account…</p>;
  if (!session) return <div className="rounded-2xl border border-blush bg-white p-6"><p className="text-charcoal/75">Sign in first so the application and future dashboard belong to your verified account.</p><Button asChild className="mt-4 bg-[#5f8769] text-white"><Link href={`${ROUTES.login}?next=${encodeURIComponent(ROUTES.affiliates)}`}>Sign in to apply</Link></Button></div>;
  if (status) return <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6"><p className="font-semibold capitalize text-emerald-900">Application status: {status}</p><p className="mt-2 text-sm text-emerald-800">Approved partners complete tax and payout onboarding before links can earn commission.</p><Button asChild variant="outline" className="mt-4"><Link href={ROUTES.accountAffiliate}>Open affiliate dashboard</Link></Button></div>;

  const checks = [
    ['legalTermsAccepted', 'I accept the affiliate program terms and payout rules.'],
    ['disclosureAccepted', 'I will clearly disclose that I may earn a commission.'],
    ['antiSpamAccepted', 'I will not send spam, make false claims, or use prohibited traffic.'],
  ] as const;
  return <div className="space-y-4 rounded-2xl border border-blush bg-white p-6 shadow-sm">
    <Input placeholder="Your name or brand" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
    <div className="grid gap-4 md:grid-cols-2"><Input placeholder="Website URL (optional)" value={form.websiteUrl} onChange={(event) => setForm({ ...form, websiteUrl: event.target.value })} /><Input placeholder="Primary social URL (optional)" value={form.socialUrl} onChange={(event) => setForm({ ...form, socialUrl: event.target.value })} /></div>
    <Textarea placeholder="Describe your audience (at least 20 characters)" value={form.audienceDescription} onChange={(event) => setForm({ ...form, audienceDescription: event.target.value })} />
    <Textarea placeholder="How will you promote the books? (at least 20 characters)" value={form.promotionPlan} onChange={(event) => setForm({ ...form, promotionPlan: event.target.value })} />
    {checks.map(([field, label]) => <label key={field} className="flex items-start gap-3 text-sm leading-6 text-charcoal/75"><Checkbox checked={form[field]} onCheckedChange={(checked) => setForm({ ...form, [field]: checked === true })} /><span>{label}</span></label>)}
    {error ? <p role="alert" className="text-sm text-red-700">{error}</p> : null}
    <Button type="button" disabled={working} onClick={() => void submit()} className="bg-[#5f8769] text-white hover:bg-[#547a5f]">{working ? 'Submitting…' : 'Submit application'}</Button>
  </div>;
}
